// ===== 登录态上下文 =====
// 包裹整个应用：未登录 → LoginGate；已登录 → 正常渲染。
// 网络校验策略（服务器不可达时的降级）：
//   - 本机有缓存会话（token+user）→ 保留会话直接进入（离线开单不中断），
//     网络恢复后下一次校验失败会自动踢回登录页；
//   - 无缓存会话 → 停在登录页（登录必须经过服务器验证密码）。

import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import {
  loadSession, verifySession, clearSession, AUTH_EVENT,
  type AuthSession, type AuthUser,
} from '@/lib/authApi';
import { prepareSyncForAccount, syncNow, syncFullNow } from '@/lib/syncEngine';
import { cleanupConfirmedDemoData } from '@/lib/demoDataCleanup';

interface AuthCtx {
  status: 'checking' | 'login' | 'ready';
  user: AuthUser | null;
  offline: boolean; // 服务器不可达但凭缓存会话进入
  /** 登录成功（LoginGate 调用后由上下文统一接管状态） */
  onLoggedIn: (s: AuthSession) => Promise<void>;
  /** 退出登录：清会话回到登录页 */
  logoutNow: () => void;
  /** token 被服务端拒绝（401）后强制回登录页 */
  forceLogin: () => void;
}

const Ctx = createContext<AuthCtx | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<'checking' | 'login' | 'ready'>('checking');
  const [user, setUser] = useState<AuthUser | null>(null);
  const [offline, setOffline] = useState(false);
  const tokenRef = useRef<string>('');

  // 启动校验：有缓存会话 → verify；无 → 登录页
  useEffect(() => {
    let alive = true;
    (async () => {
      const s = loadSession();
      if (!s) { if (alive) setStatus('login'); return; }
      tokenRef.current = s.token;
      try {
        const u = await verifySession(s.token);
        if (!alive) return;
        if (u) {
          const accountChanged = await prepareSyncForAccount(u.username);
          cleanupConfirmedDemoData(localStorage);
          setUser(u);
          setOffline(false);
          setStatus('ready');
          if (accountChanged) void syncFullNow('cached-session-account-boundary');
        } else {
          clearSession();
          tokenRef.current = '';
          setStatus('login');
        }
      } catch {
        // 服务器不可达：凭缓存会话离线进入（保业务连续性）
        if (!alive) return;
        const accountChanged = await prepareSyncForAccount(s.user.username);
        cleanupConfirmedDemoData(localStorage);
        setUser(s.user);
        setOffline(true);
        setStatus('ready');
        if (accountChanged) void syncFullNow('offline-session-account-boundary');
      }
    })();
    return () => { alive = false; };
  }, []);

  const onLoggedIn = useCallback(async (s: AuthSession) => {
    const accountChanged = await prepareSyncForAccount(s.user.username);
    tokenRef.current = s.token;
    cleanupConfirmedDemoData(localStorage);
    setUser(s.user);
    setOffline(false);
    setStatus('ready');
    if (accountChanged) await syncFullNow('account-switch-full');
    else await syncNow('login-incremental');
  }, []);

  const logoutNow = useCallback(() => {
    clearSession();
    tokenRef.current = '';
    setUser(null);
    setStatus('login');
  }, []);

  const forceLogin = useCallback(() => {
    clearSession();
    tokenRef.current = '';
    setUser(null);
    setOffline(false);
    setStatus('login');
  }, []);

  // 其他标签页登录/注销同步
  useEffect(() => {
    const h = () => {
      const s = loadSession();
      if (!s) { logoutNow(); }
    };
    window.addEventListener(AUTH_EVENT, h);
    return () => window.removeEventListener(AUTH_EVENT, h);
  }, [logoutNow]);

  return (
    <Ctx.Provider value={{ status, user, offline, onLoggedIn, logoutNow, forceLogin }}>
      {children}
    </Ctx.Provider>
  );
}

export function useAuth(): AuthCtx {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAuth 必须在 AuthProvider 内使用');
  return v;
}

export function getAuthToken(): string {
  return loadSession()?.token || '';
}
